import { useRef, useState } from 'preact/hooks';
import { command } from '../api/client.js';
import { useCommand } from '../api/resource.js';
import { useSession } from '../session/session.jsx';
import { Button, Field, Form, Textarea } from '../ui/primitives.jsx';
import { ErrorView } from '../ui/states.jsx';

export function QuestionDecision({ question, spaceId, writable, name }) {
  const session = useSession();
  const [body, setBody] = useState('');
  const lock = useRef(false);
  const intent = useRef(null);
  const cmd = useCommand((params, idempotencyKey) => command(params.operation, params.body, { nodeId: session.nodeId, idempotencyKey }), {
    invalidates: [`space:${spaceId}:task`, `space:${spaceId}:inbox`, `space:${spaceId}:conversation-activity`],
  });
  const uncertain = cmd.error && !(cmd.error.status >= 400 && cmd.error.status < 500 && cmd.error.code);
  const allowed = writable && question.state === 'open' && !cmd.done && cmd.error?.status !== 403;
  const canAnswer = allowed && question.recipientNodeIds.includes(session.nodeId);
  const canWithdraw = allowed && question.openedBy === session.nodeId;
  const submit = async (operation) => {
    if (lock.current || !(operation === 'question_answer' ? canAnswer && body.trim() : canWithdraw)) return;
    if (intent.current && intent.current.operation !== operation) return;
    lock.current = true;
    try {
      if (!intent.current) intent.current = { operation, body: { spaceId, questionId: question.questionId,
        ...(operation === 'question_answer' ? { body: body.trim() } : {}) } };
      const result = await cmd.run(intent.current);
      if (result.ok || (result.error?.status >= 400 && result.error?.status < 500 && result.error?.code)) intent.current = null;
    } finally { lock.current = false; }
  };
  return <article class="stack">
    <p class="t-body-strong">{question.prompt}</p>
    <p class="t-meta">{name(question.openedBy)}의 질문 · {question.state === 'open' ? '응답 기다리는 중' : question.state === 'answered' ? '답변됨' : '철회됨'}</p>
    {(question.answers ?? []).map((answer) => <p class="t-body" key={answer.answerId}>{name(answer.actorNodeId)} · {answer.body}</p>)}
    {canAnswer ? <Form class="stack" busy={cmd.pending} onSubmit={() => submit('question_answer')}>
      <Field label="답변" htmlFor={`question-answer-${question.questionId}`} required>
        <Textarea id={`question-answer-${question.questionId}`} value={body} disabled={cmd.pending || uncertain}
          onInput={(e) => setBody(e.currentTarget.value)} />
      </Field>
      <Button kind="submit" size="sm" busy={cmd.pending} disabled={cmd.blocked || !body.trim() || (uncertain && intent.current?.operation !== 'question_answer')}>{uncertain && intent.current?.operation === 'question_answer' ? '같은 답변 다시 확인' : '답변 남기기'}</Button>
    </Form> : null}
    {canWithdraw ? <Button size="sm" disabled={cmd.blocked || cmd.pending || (uncertain && intent.current?.operation !== 'question_withdraw')}
      onClick={() => submit('question_withdraw')}>{uncertain ? '같은 철회 다시 확인' : '질문 철회'}</Button> : null}
    {cmd.error ? <ErrorView error={cmd.error} /> : null}
    {cmd.done ? <p role="status" class="t-meta">응답이 기록됐습니다.</p> : null}
  </article>;
}
